import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Scale, MessageSquare, Users, FileCheck, Bell, Calendar,
  TrendingUp, Clock, Award, Target, ArrowRight, User,
  Briefcase, CheckCircle2, AlertCircle, Mail, Phone
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getUser } from '../utils/auth';
import api from '../utils/api';

const ModernDashboard = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  
  const [stats, setStats] = useState({
    cases: 0,
    tickets: 0,
    employees: 0,
    pendingRequests: 0
  });
  const [notifications, setNotifications] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch stats and notifications
      const [notifRes] = await Promise.all([
        api.get('/notifications').catch(() => ({ data: [] }))
      ]);
      
      setNotifications(notifRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  // Main Services
  const mainServices = [
    {
      id: 'cases',
      title: i18n.language === 'ar' ? 'القضايا' : 'Cases',
      description: i18n.language === 'ar' ? 'إدارة القضايا والمتابعة' : 'Case management and tracking',
      icon: Scale,
      gradient: 'from-gray-700 to-gray-900',
      bgColor: 'bg-gray-50',
      path: '/admin/cases-advanced',
      stats: stats.cases,
      statsLabel: i18n.language === 'ar' ? 'قضية' : 'cases'
    },
    {
      id: 'tickets',
      title: i18n.language === 'ar' ? 'التذاكر' : 'Tickets',
      description: i18n.language === 'ar' ? 'نظام الدعم والمتابعة' : 'Support and tracking system',
      icon: MessageSquare,
      gradient: 'from-amber-600 to-amber-700',
      bgColor: 'bg-amber-50',
      path: '/tickets',
      stats: stats.tickets,
      statsLabel: i18n.language === 'ar' ? 'تذكرة' : 'tickets'
    },
    {
      id: 'hr',
      title: i18n.language === 'ar' ? 'الموارد البشرية' : 'Human Resources',
      description: i18n.language === 'ar' ? 'إدارة الموظفين والطلبات' : 'Employee and request management',
      icon: Users,
      gradient: 'from-slate-700 to-slate-900',
      bgColor: 'bg-slate-50',
      path: '/hr/employees',
      stats: stats.employees,
      statsLabel: i18n.language === 'ar' ? 'موظف' : 'employees'
    },
    {
      id: 'documents',
      title: i18n.language === 'ar' ? 'المستندات' : 'Documents',
      description: i18n.language === 'ar' ? 'إدارة الوثائق والملفات' : 'Document and file management',
      icon: FileCheck,
      gradient: 'from-yellow-600 to-yellow-700',
      bgColor: 'bg-yellow-50',
      path: '/documents',
      stats: 0,
      statsLabel: i18n.language === 'ar' ? 'مستند' : 'documents'
    }
  ];

  // Quick Services
  const quickServices = [
    {
      title: i18n.language === 'ar' ? 'إضافة قضية' : 'Add Case',
      description: i18n.language === 'ar' ? 'إنشاء ملف قضية جديد' : 'Create new case file',
      icon: Scale,
      path: '/admin/cases-advanced',
      color: 'text-gray-700'
    },
    {
      title: i18n.language === 'ar' ? 'إضافة موظف' : 'Add Employee',
      description: i18n.language === 'ar' ? 'تسجيل موظف جديد' : 'Register new employee',
      icon: User,
      path: '/hr/employees/new',
      color: 'text-amber-600'
    },
    {
      title: i18n.language === 'ar' ? 'طلب جديد' : 'New Request',
      description: i18n.language === 'ar' ? 'تقديم طلب إداري' : 'Submit administrative request',
      icon: FileCheck,
      path: '/hr/requests/new',
      color: 'text-slate-700'
    },
    {
      title: i18n.language === 'ar' ? 'إنشاء تذكرة' : 'Create Ticket',
      description: i18n.language === 'ar' ? 'فتح تذكرة دعم' : 'Open support ticket',
      icon: MessageSquare,
      path: '/tickets/create',
      color: 'text-yellow-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content - 2 columns */}
            <div className="lg:col-span-2 space-y-6">
              {/* User Profile Card */}
              <Card className="shadow-lg border-0 bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden">
                <CardContent className="p-8 relative">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
                  <div className="relative flex items-center gap-6">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-3xl font-bold shadow-xl">
                      {user?.full_name?.charAt(0) || 'W'}
                    </div>
                    <div className="flex-1">
                      <h2 className="text-3xl font-bold mb-2">
                        {i18n.language === 'ar' ? 'مرحباً,' : 'Welcome,'} {user?.full_name}
                      </h2>
                      <p className="text-gray-300 mb-3">
                        {user?.email}
                      </p>
                      <div className="flex items-center gap-4">
                        <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                          {user?.role === 'admin' ? (i18n.language === 'ar' ? 'مدير' : 'Admin') : 
                           user?.role === 'lawyer' ? (i18n.language === 'ar' ? 'محامي' : 'Lawyer') :
                           (i18n.language === 'ar' ? 'عميل' : 'Client')}
                        </Badge>
                        <span className="text-sm text-gray-400">
                          {i18n.language === 'ar' ? 'آخر دخول: اليوم' : 'Last login: Today'}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Main Services */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {i18n.language === 'ar' ? 'الخدمات الرئيسية' : 'Main Services'}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {mainServices.map((service) => {
                    const Icon = service.icon;
                    return (
                      <Card
                        key={service.id}
                        onClick={() => navigate(service.path)}
                        className={`cursor-pointer hover:shadow-2xl transition-all duration-300 border-0 ${service.bgColor}`}
                      >
                        <CardContent className="p-6">
                          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-4 shadow-lg`}>
                            <Icon className="w-8 h-8 text-white" />
                          </div>
                          <h4 className="text-xl font-bold text-gray-900 mb-2">
                            {service.title}
                          </h4>
                          <p className="text-sm text-gray-600 mb-4">
                            {service.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-2xl font-bold text-gray-900">
                                {service.stats}
                              </p>
                              <p className="text-xs text-gray-500">{service.statsLabel}</p>
                            </div>
                            <ArrowRight className="w-5 h-5 text-gray-400" />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Quick Services */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {i18n.language === 'ar' ? 'خدمات سريعة' : 'Quick Services'}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {quickServices.map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <Card
                        key={index}
                        onClick={() => navigate(service.path)}
                        className="cursor-pointer hover:shadow-xl transition-all duration-300 border bg-white"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0`}>
                              <Icon className={`w-6 h-6 ${service.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="font-bold text-gray-900 mb-1 truncate">
                                {service.title}
                              </h5>
                              <p className="text-xs text-gray-600 line-clamp-2">
                                {service.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sidebar - 1 column */}
            <div className="space-y-6">
              {/* Notifications */}
              <Card className="shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-amber-600" />
                    {i18n.language === 'ar' ? 'آخر الإشعارات' : 'Recent Notifications'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {notifications.length === 0 ? (
                    <div className="text-center py-8">
                      <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 text-sm">
                        {i18n.language === 'ar' ? 'لا توجد إشعارات' : 'No notifications'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notifications.map((notif, index) => (
                        <div
                          key={index}
                          className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                          <div className="flex items-start gap-2">
                            <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {notif.title}
                              </p>
                              <p className="text-xs text-gray-600 line-clamp-2 mt-1">
                                {notif.message}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button
                    onClick={() => navigate('/notifications')}
                    variant="outline"
                    className="w-full mt-4"
                  >
                    {i18n.language === 'ar' ? 'عرض الكل' : 'View All'}
                  </Button>
                </CardContent>
              </Card>

              {/* Calendar / Upcoming Events */}
              <Card className="shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-slate-700" />
                    {i18n.language === 'ar' ? 'المواعيد القادمة' : 'Upcoming Events'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 text-sm">
                      {i18n.language === 'ar' ? 'لا توجد مواعيد' : 'No upcoming events'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card className="shadow-lg border-0 bg-gradient-to-br from-amber-50 to-yellow-50">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 font-medium">
                        {i18n.language === 'ar' ? 'الطلبات المعلقة' : 'Pending Requests'}
                      </span>
                      <span className="text-2xl font-bold text-amber-600">
                        {stats.pendingRequests}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 font-medium">
                        {i18n.language === 'ar' ? 'المهام اليوم' : 'Tasks Today'}
                      </span>
                      <span className="text-2xl font-bold text-slate-700">0</span>
                    </div>
                    <Button
                      onClick={() => navigate('/hr/requests')}
                      className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800"
                    >
                      {i18n.language === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ModernDashboard;
